import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CustomCircularProgress from '../ui/customCircularProgress/customCircularProgress.component';
import { useCRUD } from '../../api/crud';
import {
  CLIENTS_ENDPOINT,
  ERROR_SNACKBAR,
  PORTAL_CONTACTS_ENDPOINT,
  SUCCESS_SNACKBAR,
} from '../../consts';

type Props = {
  clientId: string | number;
  /** Omitted when granting access to someone new. */
  contactId?: string | number;
  onSuccess: () => void;
};

const extractError = (errors: Record<string, unknown> | undefined, key: string): string => {
  const value = errors?.[key];
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && value.length > 0) {
    const first = value[0];
    if (typeof first === 'string') return first;
    if (first && typeof first === 'object' && 'message' in first) {
      return (first as { message?: string }).message ?? '';
    }
  }
  return '';
};

export const ClientPortalContactForm = ({ clientId, contactId, onSuccess }: Props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { create, update, get } = useCRUD(PORTAL_CONTACTS_ENDPOINT);
  const { get: getClient } = useCRUD(CLIENTS_ENDPOINT);

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, unknown>>({});

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);

      if (contactId) {
        const response = await get(contactId);
        if (active && response?.data) {
          setEmail(response.data.email ?? '');
          setFullName(response.data.full_name ?? '');
          setIsActive(Boolean(response.data.is_active));
        }
      } else {
        // Granting access to someone new: the client's own contact address is
        // almost always the one that should receive the login codes, so it is
        // offered as a starting point rather than making staff retype it.
        const response = await getClient(clientId);
        if (active && response?.data?.address?.email) {
          setEmail(response.data.address.email);
        }
      }

      if (active) {
        setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, contactId]);

  const handleSubmit = async () => {
    setSaving(true);
    setErrors({});

    const payload = {
      client: clientId,
      email,
      full_name: fullName,
      is_active: isActive,
    };

    const response = contactId
      ? await update({ id: contactId, ...payload })
      : await create(payload);

    setSaving(false);

    if (response?.success === false) {
      setErrors(response.errorData ?? {});
      enqueueSnackbar(t('Could not save the portal contact.'), ERROR_SNACKBAR);
      return;
    }

    enqueueSnackbar(t('Portal contact saved.'), SUCCESS_SNACKBAR);
    onSuccess();
  };

  if (loading) {
    return <CustomCircularProgress />;
  }

  const generalError = extractError(errors, 'detail') || extractError(errors, 'non_field_errors');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t('This address receives the one-time login code.')}
      </Typography>

      <TextField
        label={t('Email address')}
        type="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        error={Boolean(extractError(errors, 'email'))}
        helperText={extractError(errors, 'email')}
        autoFocus
        fullWidth
      />
      <TextField
        label={t('Full name')}
        value={fullName}
        onChange={(event) => setFullName(event.target.value)}
        error={Boolean(extractError(errors, 'full_name'))}
        helperText={extractError(errors, 'full_name')}
        fullWidth
      />
      <FormControlLabel
        control={
          <Switch checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />
        }
        label={t('Active')}
      />

      {generalError && <Alert severity="error">{generalError}</Alert>}

      <Button variant="contained" onClick={handleSubmit} disabled={saving || !email}>
        {saving ? t('Saving...') : t('Save')}
      </Button>
    </Box>
  );
};

export default ClientPortalContactForm;
